import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { AutoComplete } from 'primereact/autocomplete';
import '../../css/Pages.css';
import BookCompilationManagementListing from '../../components/RegisterManagement/BookCompilationManagement/BookCompilationManagementListing';
import { useConfirmDeleteDialog } from '../../hooks/useConfirmDeleteDialog';
import BookCompilationForm from '../../components/Register/BookCompilation/BookCompilationForm';
import { downloadExcel } from '../../utils/excelUtils';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className, ...rest }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            placeholder={placeholder}
            className={className || "custom-dropdown"}
            panelClassName={panelUniqueClass}
            style={{ width: '100%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
            {...rest}
        />
    );
};

const BookCompilationManagement = () => {
    const toast = useRef(null);
    const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);

    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [departments, setDepartments] = useState([]);
    const [projectTypes, setProjectTypes] = useState([]);
    const [educationLevels, setEducationLevels] = useState([]);
    const [jobTitles, setJobTitles] = useState([]);

    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [formData, setFormData] = useState({});
    const [members, setMembers] = useState([]);
    const [users, setUsers] = useState([]);

    const [isEditRequestModalOpen, setIsEditRequestModalOpen] = useState(false);
    const [editRequestContent, setEditRequestContent] = useState('');
    const [selectedProposalForEdit, setSelectedProposalForEdit] = useState(null);

    const [filters, setFilters] = useState({
        department: '',
        authorId: null,
        projectType: '',
        educationLevel: '',
        jobTitle: '',
        generalSearch: '',
        status: '',
        fromDate: null,
        toDate: null
    });

    const [sortConfig, setSortConfig] = useState({ key: 'BookName', direction: 'asc' });

    const [selectedAuthor, setSelectedAuthor] = useState(null);
    const [filteredAuthors, setFilteredAuthors] = useState([]);

    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [previewExportData, setPreviewExportData] = useState(null);
    const [exportFilters, setExportFilters] = useState({
        fromDate: null,
        toDate: null,
        projectType: '',
        userId: ''
    });

    const [isDesktop, setIsDesktop] = useState(true);

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth > 992);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const { confirmDeleteDialog } = useConfirmDeleteDialog();

    const currentUser = useMemo(() => JSON.parse(localStorage.getItem('user')) || {}, []);
    const userRoleName = useMemo(() => (currentUser.RoleName || '').toLowerCase(), [currentUser.RoleName]);

    const isManager = currentUser?.RoleId === 1 || userRoleName === 'quản trị viên' ||
        currentUser?.RoleId === 11 || userRoleName.includes('quản lý khoa');

    const canModify = currentUser?.RoleId === 1 || userRoleName === 'quản trị viên' ||
        ((currentUser?.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser?.IsDelegatedAdmin === true);

    const authHeaders = useMemo(() => ({
        'X-User-Role-Id': currentUser.RoleId ? currentUser.RoleId.toString() : '',
        'X-User-Dept-Id': currentUser.DepartmentId ? currentUser.DepartmentId.toString() : '',
        'X-User-Id': currentUser.Id ? currentUser.Id.toString() : '',
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
    }), [currentUser]);

    const fetchDropdowns = useCallback(async () => {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        try {
            const [deptRes, ptRes, elRes, jtRes, userRes] = await Promise.all([
                fetch(`${baseUrl}/department`, { headers: authHeaders }),
                fetch(`${baseUrl}/projecttype`, { headers: authHeaders }),
                fetch(`${baseUrl}/educationlevel`, { headers: authHeaders }),
                fetch(`${baseUrl}/jobtitle`, { headers: authHeaders }),
                fetch(`${baseUrl}/user`, { headers: authHeaders })
            ]);
            if (deptRes.ok) setDepartments(await deptRes.json());
            if (ptRes.ok) setProjectTypes(await ptRes.json());
            if (elRes.ok) setEducationLevels(await elRes.json());
            if (jtRes.ok) setJobTitles(await jtRes.json());
            if (userRes.ok) setUsers(await userRes.json());
        } catch (error) {
            console.error("Lỗi tải danh mục:", error);
        }
    }, [authHeaders]);

    const fetchData = useCallback(async (isBackground = false) => {
        if (!isBackground) setIsLoading(true);
        try {
            if (!currentUser?.Id) return;

            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/bookcompilationmanagement`,
                {
                    method: 'GET',
                    headers: authHeaders
                }
            );

            if (response.ok) {
                const result = await response.json();
                setData(result);
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi tải dữ liệu!', life: 3000 });
        } finally {
            if (!isBackground) setIsLoading(false);
        }
    }, [currentUser?.Id, authHeaders]);

    useEffect(() => {
        fetchData();
        fetchDropdowns();
    }, [fetchData, fetchDropdowns]);

    const userOptions = useMemo(() => {
        return users.map(u => {
            const labelStr = `${u.LastName || ''} ${u.FirstName || ''} - ${u.Email || ''}`.trim();
            return {
                label: labelStr,
                searchKey: superCleanString(labelStr),
                value: u.Id
            };
        });
    }, [users]);

    const userFilterTemplate = (option) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName}</span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                    {option.email} {option.dept ? ` - ${option.dept}` : ''}
                </span>
            </div>
        );
    };

    const filterUserOptions = useMemo(() => {
        return users.map(u => {
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || '';
            const dept = u.DepartmentName || '';
            const rawSearchStr = `${fullName} ${email} ${dept}`;

            return {
                label: fullName,
                searchKey: superCleanString(rawSearchStr),
                value: u.Id,
                fullName: fullName,
                email: email,
                dept: dept
            };
        });
    }, [users]);

    const searchAuthor = (event) => {
        const query = superCleanString(event.query);
        let _filteredAuthors;

        if (!query.trim().length) {
            _filteredAuthors = [...filterUserOptions];
        } else {
            _filteredAuthors = filterUserOptions.filter((user) => {
                return user.searchKey.includes(query);
            });
        }
        setFilteredAuthors(_filteredAuthors);
    };

    const filteredData = useMemo(() => {
        let allowedDeptNames = [];
        if (filters.department) {
            const selectedDept = departments.find(d => d.Name.trim() === filters.department.trim());

            if (selectedDept) {
                allowedDeptNames.push(selectedDept.Name.trim());

                const findChildren = (parentId) => {
                    const children = departments.filter(d => d.ParentId === parentId);
                    children.forEach(child => {
                        allowedDeptNames.push(child.Name.trim());
                        findChildren(child.Id);
                    });
                };

                findChildren(selectedDept.Id);
            } else {
                allowedDeptNames.push(filters.department.trim());
            }
        }

        let result = data.filter(item => {
            if (filters.status && item.Status !== filters.status) return false;

            if (filters.department) {
                const itemDeptName = item.DepartmentName ? item.DepartmentName.trim() : "";
                if (!allowedDeptNames.includes(itemDeptName)) {
                    return false;
                }
            }

            if (filters.projectType && item.ProjectType !== filters.projectType && item.ProjectTypeName !== filters.projectType) return false;
            if (filters.educationLevel && item.EducationLevel !== filters.educationLevel) return false;
            if (filters.jobTitle && item.JobTitle !== filters.jobTitle) return false;

            if (item.StartDate) {
                const itemStartDate = new Date(item.StartDate);
                itemStartDate.setHours(0, 0, 0, 0);

                if (filters.fromDate) {
                    const filterFrom = new Date(filters.fromDate);
                    filterFrom.setHours(0, 0, 0, 0);
                    if (itemStartDate < filterFrom) return false;
                }
                if (filters.toDate) {
                    const filterTo = new Date(filters.toDate);
                    filterTo.setHours(0, 0, 0, 0);
                    if (itemStartDate > filterTo) return false;
                }
            } else if (filters.fromDate || filters.toDate) {
                return false;
            }

            if (filters.authorId) {
                const targetId = filters.authorId.toString();
                let isMatch = item.UserId?.toString() === targetId;

                if (!isMatch) {
                    try {
                        const mList = JSON.parse(item.MembersJSON || '[]');
                        isMatch = mList.some(m => (m.UserId || m.userId || m.Id)?.toString() === targetId);
                    } catch (e) {
                        isMatch = false;
                    }
                }

                if (!isMatch) return false;
            }

            if (filters.generalSearch) {
                const query = superCleanString(filters.generalSearch);
                const bookName = superCleanString(item.BookName);
                const bookTitle = superCleanString(item.Title);
                if (!bookName.includes(query) && !bookTitle.includes(query)) return false;
            }

            return true;
        });

        if (sortConfig !== null) {
            result.sort((a, b) => {
                const valA = (a.BookName || a.Title || '').toString().trim();
                const valB = (b.BookName || b.Title || '').toString().trim();
                const comparison = valA.localeCompare(valB, 'vi', { sensitivity: 'base' });
                return sortConfig.direction === 'asc' ? comparison : -comparison;
            });
        }

        return result;

    }, [data, filters, departments, sortConfig]);

    const handleFilterChange = useCallback((e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    }, []);

    const checkAndFetchMissingUsers = useCallback(async (parsedMembers) => {
        const missingIds = parsedMembers
            .map(m => m.userId || m.UserId || m.Id || m.id)
            .filter(id => id && !users.some(u => u.Id.toString() === id.toString()));

        if (missingIds.length > 0) {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user?includeIds=${missingIds.join(',')}`, {
                    headers: authHeaders
                });
                if (res.ok) {
                    const extraUsers = await res.json();
                    setUsers(prevUsers => {
                        const merged = [...prevUsers];
                        extraUsers.forEach(eu => {
                            if (!merged.some(u => u.Id === eu.Id)) merged.push(eu);
                        });
                        return merged;
                    });
                }
            } catch (error) {
                console.error("Lỗi khi tải thông tin người dùng bổ sung:", error);
            }
        }
    }, [users, authHeaders]);

    const handleViewDetail = useCallback(async (item) => {
        let parsedMembers = [];
        try {
            parsedMembers = JSON.parse(item.MembersJSON || '[]');
        } catch (e) {
            parsedMembers = [];
        }

        await checkAndFetchMissingUsers(parsedMembers);

        setFormData({ ...item });
        setMembers(parsedMembers);
        setIsViewModalOpen(true);
    }, [checkAndFetchMissingUsers]);

    const handleStatusChange = useCallback(async (id, newStatus) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền phê duyệt/từ chối!', life: 3000 });
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/bookcompilationmanagement`, {
                method: 'PATCH',
                headers: {
                    ...authHeaders,
                    'Content-Type': 'application/json; charset=UTF-8'
                },
                body: JSON.stringify({ id: id, status: newStatus })
            });

            if (response.ok) {
                const messages = {
                    'Đã duyệt': "Phê duyệt thành công!",
                    'Từ chối': "Đã từ chối đăng ký!",
                    'Đã gửi duyệt': "Đã hủy phê duyệt thành công!"
                };
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: messages[newStatus] || 'Cập nhật trạng thái thành công!', life: 3000 });
                fetchData(true);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Thao tác thất bại!', life: 3000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!' });
        }
    }, [canModify, authHeaders, fetchData]);

    const handleBulkStatusChange = async (ids, newStatus) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền phê duyệt!', life: 3000 });
        }

        if (newStatus === 'Đã duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status === 'Đã duyệt');
            if (invalidItems.length > 0) {
                return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã duyệt để thao tác!', life: 4000 });
            }
        } else if (newStatus === 'Đã gửi duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status !== 'Đã duyệt');
            if (invalidItems.length > 0) {
                return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Chỉ có thể hủy đối với hồ sơ có trạng thái "Đã duyệt"!', life: 4000 });
            }
        }

        setIsLoading(true);
        try {
            const promises = ids.map(id =>
                fetch(`${process.env.REACT_APP_API_BASE_URL}/bookcompilationmanagement`, {
                    method: 'PATCH',
                    headers: { ...authHeaders, 'Content-Type': 'application/json; charset=UTF-8' },
                    body: JSON.stringify({ id, status: newStatus })
                })
            );

            const results = await Promise.all(promises);
            const allOk = results.every(res => res.ok);

            if (allOk) {
                toast.current?.show({
                    severity: 'success', summary: 'Thành công',
                    detail: newStatus === 'Đã duyệt' ? `Đã phê duyệt ${ids.length} mục!` : `Đã hủy duyệt ${ids.length} mục!`, life: 3000
                });
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: "Một số thao tác thất bại!", life: 3000 });
            }
            fetchData(true);
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenRequestEdit = useCallback((item) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền yêu cầu chỉnh sửa!', life: 3000 });
        }
        setSelectedProposalForEdit(item);
        setEditRequestContent('');
        setIsEditRequestModalOpen(true);
    }, [canModify]);

    const handleSendEditRequest = async (e) => {
        e.preventDefault();
        if (!editRequestContent.trim()) {
            toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng nhập nội dung yêu cầu!', life: 3000 });
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/bookcompilationmanagement`, {
                method: 'PATCH',
                headers: {
                    ...authHeaders,
                    'Content-Type': 'application/json; charset=UTF-8'
                },
                body: JSON.stringify({
                    id: selectedProposalForEdit.Id,
                    status: 'Yêu cầu chỉnh sửa',
                    note: editRequestContent
                })
            });

            if (response.ok) {
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã gửi Email yêu cầu chỉnh sửa!', life: 3000 });
                setIsEditRequestModalOpen(false);
                fetchData(true);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Gửi yêu cầu thất bại!', life: 3000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        }
    };

    const handleDelete = useCallback((id) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem, không được phép xóa!', life: 3000 });
        }

        const item = data.find(d => d.Id === id);
        if (item?.Status === 'Đã gửi duyệt' || item?.Status === 'Đã duyệt') {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không thể xóa khi đang chờ duyệt hoặc đã duyệt. Hãy hủy duyệt trước!', life: 4000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa',
            message: 'Bạn có chắc chắn muốn xóa đăng ký biên soạn này khỏi hệ thống?',
            accept: async () => {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/bookcompilationmanagement?id=${id}`, {
                        method: 'DELETE',
                        headers: authHeaders
                    });

                    if (response.ok) {
                        toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã xóa đăng ký!' });
                        fetchData(true);
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Xóa thất bại!' });
                    }
                } catch (e) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!' });
                }
            }
        });
    }, [canModify, data, authHeaders, fetchData, confirmDeleteDialog]);

    const handleBulkDelete = (ids) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem, không được phép xóa!', life: 3000 });
        }
        const invalidItems = data.filter(d => ids.includes(d.Id) && (d.Status === 'Đã gửi duyệt' || d.Status === 'Đã duyệt'));
        if (invalidItems.length > 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã gửi hoặc đã duyệt khỏi danh sách cần xóa!', life: 4000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa hàng loạt',
            message: `Bạn có chắc chắn muốn xóa ${ids.length} đăng ký đã chọn? Dữ liệu bên Giảng viên cũng sẽ bị xóa.`,
            accept: async () => {
                setIsLoading(true);
                try {
                    const promises = ids.map(id =>
                        fetch(`${process.env.REACT_APP_API_BASE_URL}/bookcompilationmanagement?id=${id}`, {
                            method: 'DELETE',
                            headers: authHeaders
                        })
                    );

                    const results = await Promise.all(promises);
                    const allOk = results.every(res => res.ok);

                    if (allOk) {
                        toast.current?.show({ severity: 'success', summary: 'Thành công', detail: `Đã xóa ${ids.length} bản ghi!` });
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Một số bản ghi xóa thất bại!' });
                    }
                    fetchData(true);
                } catch (error) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const executeExport = useCallback((dataToExport, fileSuffix) => {
        if (dataToExport.length === 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có dữ liệu để xuất!', life: 3000 });
        }

        const formattedData = dataToExport.map((item, index) => ({
            'STT': index + 1,
            'Giảng viên': item.FullName || item.fullName || '',
            'Đơn vị': item.DepartmentName || item.departmentName || item.Department || '',
            'Tên sách/giáo trình': item.BookName || item.bookName || item.Title || '',
            'Loại công trình': item.ProjectTypeName || item.projectTypeName || item.ProjectType || item.projectType || '',
            'Loại biên soạn': item.IsFirstTime ? 'Biên soạn lần đầu' : 'Viết lại',
            'Mã học phần': item.CourseCode || item.courseCode || '',
            'Thời gian thực hiện': `${item.EstimatedMonths || 0} tháng`,
            'Trạng thái': item.Status || item.status || 'Lưu nháp',
            'Ngày cập nhật': item.UpdatedDate ? new Date(item.UpdatedDate).toLocaleDateString('vi-VN') : ''
        }));

        const summaryRow = {
            'STT': '', 'Giảng viên': 'TỔNG CỘNG:', 'Đơn vị': `${dataToExport.length} Bản đăng ký`,
            'Tên sách/giáo trình': '', 'Loại công trình': '', 'Loại biên soạn': '',
            'Mã học phần': '', 'Thời gian thực hiện': '', 'Trạng thái': '', 'Ngày cập nhật': ''
        };

        const colWidths = [
            { wch: 5 }, { wch: 25 }, { wch: 20 }, { wch: 45 }, { wch: 20 },
            { wch: 20 }, { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 15 }
        ];

        downloadExcel({
            data: formattedData,
            fileName: `QuanLyDangKyBienSoan_${fileSuffix}`,
            sheetName: 'QuanLyDangKyBienSoan',
            colWidths: colWidths,
            summaryRow: summaryRow
        });
    }, []);

    const handleExportCurrent = () => {
        executeExport(filteredData, 'HienHanh');
        setExportDropdownOpen(false);
    };

    const getFilteredExportData = useCallback(() => {
        let result = [...data];

        if (exportFilters.fromDate) {
            const filterFrom = new Date(exportFilters.fromDate);
            filterFrom.setHours(0, 0, 0, 0);
            result = result.filter(item => item.UpdatedDate && new Date(item.UpdatedDate) >= filterFrom);
        }
        if (exportFilters.toDate) {
            const filterTo = new Date(exportFilters.toDate);
            filterTo.setHours(23, 59, 59, 999);
            result = result.filter(item => item.UpdatedDate && new Date(item.UpdatedDate) <= filterTo);
        }
        if (exportFilters.projectType) {
            result = result.filter(item => item.ProjectType === exportFilters.projectType || item.ProjectTypeName === exportFilters.projectType);
        }
        if (exportFilters.userId) {
            result = result.filter(item => {
                if (item.UserId?.toString() === exportFilters.userId.toString()) return true;
                try {
                    const membersList = JSON.parse(item.MembersJSON || '[]');
                    return membersList.some(m => (m.UserId || m.userId || m.Id)?.toString() === exportFilters.userId.toString());
                } catch (e) {
                    return false;
                }
            });
        }

        return result;
    }, [data, exportFilters]);

    const handlePreviewAdvancedFilters = () => {
        setPreviewExportData(getFilteredExportData());
    };

    const handleClearAdvancedFilters = () => {
        setExportFilters({ fromDate: null, toDate: null, projectType: '', userId: '' });
        setPreviewExportData(null);
    };

    const handleExportFromModal = () => {
        executeExport(previewExportData || getFilteredExportData(), 'LocNangCao');
        setIsExportModalOpen(false);
    };

    const renderDeptTree = (parentId = null, level = 0) => {
        return departments
            .filter(d => d.ParentId === parentId)
            .map(d => (
                <React.Fragment key={`filter-dept-${d.Id}`}>
                    <option value={d.Name}>
                        {'\u00A0'.repeat(level * 4)}{level > 0 ? '˪ ' : ''}{d.Name}
                    </option>
                    {renderDeptTree(d.Id, level + 1)}
                </React.Fragment>
            ));
    };

    return (
        <div className="page-container">
            <Toast ref={toast} position="top-right" baseZIndex={99999} style={{ zIndex: 99999 }} />

            <div className="page-header d-flex justify-content-between align-items-center mb-4">
                <div className="header-title">
                    <h2 style={{ textTransform: 'uppercase' }}>QUẢN LÝ ĐĂNG KÝ BIÊN SOẠN</h2>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: isDesktop ? 'row' : 'column', gap: '10px', marginBottom: '20px' }}>
                <div style={{ position: 'relative', display: isDesktop ? 'inline-block' : 'block', width: isDesktop ? 'auto' : '100%' }}>
                    <button
                        className="btn-add-new"
                        style={{ backgroundColor: '#1a9953', margin: 0, width: '100%' }}
                        onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                    >
                        <i className="fa-solid fa-file-excel"></i> Xuất Excel <i className="fa-solid fa-caret-down" style={{ marginLeft: '5px' }}></i>
                    </button>

                    {exportDropdownOpen && (
                        <>
                            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setExportDropdownOpen(false)}></div>
                            <div className="custom-dropdown-menu">
                                <div className="custom-dropdown-item" onClick={handleExportCurrent}>
                                    <i className="fa-solid fa-list-check" style={{ color: '#1a9953', width: '20px', fontSize: '16px' }}></i>
                                    Xuất toàn bộ dữ liệu
                                </div>
                                <div className="custom-dropdown-divider"></div>
                                <div className="custom-dropdown-item" onClick={() => {
                                    setIsExportModalOpen(true);
                                    setExportDropdownOpen(false);
                                    setPreviewExportData(null);
                                }}>
                                    <i className="fa-solid fa-filter" style={{ color: '#f39c12', width: '20px', fontSize: '16px' }}></i>
                                    Lọc dữ liệu nâng cao
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div style={{ marginBottom: '20px', backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                <div style={isDesktop
                    ? { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }
                    : { display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '15px' }
                }>
                    <p className="sub-title" style={{ margin: 0, fontWeight: 'bold', color: '#555' }}>DANH SÁCH CÔNG TRÌNH</p>
                </div>

                <div style={{
                    display: isDesktop ? 'grid' : 'flex',
                    gridTemplateColumns: isDesktop ? 'repeat(4, 1fr)' : 'none',
                    flexDirection: isDesktop ? 'none' : 'column',
                    gap: '15px'
                }}>
                    <select name="department" className="form-input" style={{ width: '100%' }} value={filters.department} onChange={handleFilterChange}>
                        <option value="">Chọn đơn vị</option>
                        {renderDeptTree()}
                    </select>

                    <div style={{ width: '100%' }}>
                        <AutoComplete
                            value={selectedAuthor}
                            suggestions={filteredAuthors}
                            completeMethod={searchAuthor}
                            field="label"
                            dropdown
                            placeholder="Họ tên giảng viên"
                            itemTemplate={userFilterTemplate}
                            onChange={(e) => {
                                setSelectedAuthor(e.value);
                                if (e.value && e.value.value) {
                                    setFilters(prev => ({ ...prev, authorId: e.value.value }));
                                } else {
                                    setFilters(prev => ({ ...prev, authorId: null }));
                                }
                            }}
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ width: '100%', padding: '0 12px', border: '1px solid #ced4da', borderRadius: '4px', fontFamily: 'inherit' }}
                            panelStyle={{ width: 'auto', minWidth: '350px', maxWidth: '500px' }}
                            emptyMessage="Không tìm thấy giảng viên"
                        />
                    </div>

                    <select name="projectType" className="form-input" style={{ width: '100%' }} value={filters.projectType} onChange={handleFilterChange}>
                        <option value="">Loại công trình</option>
                        {projectTypes.map(pt => <option key={pt.Id} value={pt.Name}>{pt.Name}</option>)}
                    </select>

                    <select name="status" className="form-input" style={{ width: '100%' }} value={filters.status} onChange={handleFilterChange}>
                        <option value="">Trạng thái</option>
                        <option value="Đã gửi duyệt">Đã gửi duyệt</option>
                        <option value="Đã duyệt">Đã duyệt</option>
                        <option value="Từ chối">Từ chối</option>
                        <option value="Yêu cầu chỉnh sửa">Yêu cầu chỉnh sửa</option>
                    </select>

                    <select name="educationLevel" className="form-input" style={{ width: '100%' }} value={filters.educationLevel} onChange={handleFilterChange}>
                        <option value="">Trình độ</option>
                        {educationLevels.map(el => <option key={el.Id} value={el.Name}>{el.Name}</option>)}
                    </select>

                    <select name="jobTitle" className="form-input" style={{ width: '100%' }} value={filters.jobTitle} onChange={handleFilterChange}>
                        <option value="">Chức danh</option>
                        {jobTitles.map(jt => <option key={jt.Id} value={jt.Name}>{jt.Name}</option>)}
                    </select>

                    <div style={{ width: '100%' }}>
                        <Calendar
                            id="fromDate"
                            value={filters.fromDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.value }))}
                            placeholder="Từ ngày"
                            dateFormat="dd/mm/yy"
                            showIcon
                            showClear
                            className="custom-prime-calendar"
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ paddingLeft: '12px' }}
                        />
                    </div>

                    <div style={{ width: '100%' }}>
                        <Calendar
                            id="toDate"
                            value={filters.toDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.value }))}
                            placeholder="Đến ngày"
                            dateFormat="dd/mm/yy"
                            showIcon
                            showClear
                            className="custom-prime-calendar"
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ paddingLeft: '12px' }}
                        />
                    </div>

                    <div className="search-wrapper" style={{ position: 'relative', width: '100%', gridColumn: isDesktop ? 'span 4' : 'none' }}>
                        <input type="text" name="generalSearch" className="form-input" style={{ width: '100%', paddingRight: '35px' }} placeholder="Tìm kiếm tên sách / giáo trình" value={filters.generalSearch} onChange={handleFilterChange} />
                        <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', right: '15px', top: '12px', color: '#999' }}></i>
                    </div>
                </div>
            </div>

            <BookCompilationManagementListing
                data={filteredData}
                filters={filters}
                onDelete={handleDelete}
                onApprove={(id) => handleStatusChange(id, 'Đã duyệt')}
                onReject={(id) => handleStatusChange(id, 'Từ chối')}
                onCancelApprove={(id) => handleStatusChange(id, 'Đã gửi duyệt')}
                onViewDetail={handleViewDetail}
                onRequestEdit={handleOpenRequestEdit}
                onBulkApprove={(ids) => handleBulkStatusChange(ids, 'Đã duyệt')}
                onBulkCancelApprove={(ids) => handleBulkStatusChange(ids, 'Đã gửi duyệt')}
                onBulkDelete={handleBulkDelete}
                isLoading={isLoading}
                toast={toast}
                sortConfig={sortConfig}
                requestSort={(key) => {
                    let direction = 'asc';
                    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
                    setSortConfig({ key, direction });
                }}
            />

            {isViewModalOpen && (
                <BookCompilationForm
                    isOpen={isViewModalOpen}
                    onClose={() => setIsViewModalOpen(false)}
                    onSubmit={(e) => e.preventDefault()}
                    formData={formData}
                    setFormData={setFormData}
                    isEditing={true}
                    isReadOnly={true}
                    projectTypes={projectTypes}
                    members={members}
                    setMembers={setMembers}
                    users={users}
                />
            )}

            {isEditRequestModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 1050 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Yêu cầu chỉnh sửa Đăng ký</h3>
                            <button className="close-btn" onClick={() => setIsEditRequestModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px' }}>
                            <form id="editRequestForm" onSubmit={handleSendEditRequest}>
                                <div className="form-group" style={{ marginBottom: '15px', backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '5px', borderLeft: '4px solid #3498db' }}>
                                    <label style={{ margin: 0, color: '#333', lineHeight: '1.5' }}>
                                        <strong>Công trình: </strong> {selectedProposalForEdit?.BookName || selectedProposalForEdit?.Title}
                                    </label>
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Nội dung cần chỉnh sửa (Sẽ gửi qua Email) *</label>
                                    <textarea
                                        className="form-input"
                                        rows="6"
                                        placeholder="Nhập chi tiết các lỗi hoặc phần cần giảng viên bổ sung"
                                        value={editRequestContent}
                                        onChange={(e) => setEditRequestContent(e.target.value)}
                                        style={{ resize: 'vertical' }}
                                        required
                                    ></textarea>
                                </div>
                            </form>
                        </div>
                        <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'flex-end' }}>
                            <button type="button" className="btn-cancel" onClick={() => setIsEditRequestModalOpen(false)} style={{ margin: 0 }}>Hủy</button>
                            <button type="submit" form="editRequestForm" className="btn-submit" style={{ backgroundColor: '#9b59b6', margin: 0 }}>
                                <i className="fa-solid fa-paper-plane" style={{ marginRight: '8px' }}></i> Gửi Email
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isExportModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 900 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '750px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Bộ Lọc & Xuất Excel nâng cao</h3>
                            <button className="close-btn" onClick={() => setIsExportModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Từ ngày (Cập nhật hồ sơ)</label>
                                    <Calendar
                                        value={exportFilters.fromDate}
                                        onChange={(e) => setExportFilters({ ...exportFilters, fromDate: e.value })}
                                        placeholder="Từ ngày"
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        showClear
                                        style={{ width: '100%' }}
                                        inputStyle={{ paddingLeft: '12px', padding: '8px' }}
                                        appendTo={document.body}
                                    />
                                </div>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Đến ngày (Cập nhật hồ sơ)</label>
                                    <Calendar
                                        value={exportFilters.toDate}
                                        onChange={(e) => setExportFilters({ ...exportFilters, toDate: e.value })}
                                        placeholder="Đến ngày"
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        showClear
                                        style={{ width: '100%' }}
                                        inputStyle={{ paddingLeft: '12px', padding: '8px' }}
                                        appendTo={document.body}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Loại công trình</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.projectType} onChange={(e) => setExportFilters({ ...exportFilters, projectType: e.target.value })}>
                                        <option value="">Toàn bộ</option>
                                        {projectTypes.map(pt => <option key={pt.Id} value={pt.Name}>{pt.Name}</option>)}
                                    </select>
                                </div>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Người dùng (Giảng viên)</label>
                                    <SearchableFocusDropdown
                                        value={exportFilters.userId}
                                        options={userOptions}
                                        onChange={(e) => setExportFilters({ ...exportFilters, userId: e.value })}
                                        placeholder="Chọn giảng viên"
                                        panelUniqueClass="custom-dropdown-panel-bookcompilation-export-advanced-filter"
                                        className="custom-dropdown"
                                        filterBy="label,searchKey"
                                        optionLabel="label"
                                    />
                                </div>
                            </div>

                            {previewExportData && (
                                <div style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                                    <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#1a9953' }}>
                                        <i className="fa-solid fa-table-list"></i> Xem trước dữ liệu: Tìm thấy {previewExportData.length} kết quả
                                    </p>

                                    <div style={{ maxHeight: '250px', overflowY: 'auto', overflowX: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                                        <table className="custom-table" style={{ width: '100%', minWidth: '500px', fontSize: '13px', margin: 0 }}>
                                            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                                                <tr>
                                                    <th width="50px" style={{ textAlign: 'center' }}>STT</th>
                                                    <th width="150px">Giảng viên</th>
                                                    <th>Tên sách/giáo trình</th>
                                                    <th width="120px">Loại công trình</th>
                                                    <th width="100px" style={{ textAlign: 'center' }}>Trạng thái</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewExportData.length > 0 ? (
                                                    previewExportData.map((item, idx) => {
                                                        return (
                                                            <tr key={item.Id}>
                                                                <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                                <td><div style={{ fontWeight: '500', color: '#333' }}>{item.FullName || item.fullName || '---'}</div></td>
                                                                <td><div style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', color: '#0056b3' }}>{item.BookName || item.bookName || item.Title}</div></td>
                                                                <td>{item.ProjectTypeName || item.projectTypeName || item.ProjectType || '---'}</td>
                                                                <td style={{ textAlign: 'center' }}>{item.Status || item.status}</td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <tr>
                                                        <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>Không tìm thấy bản ghi nào khớp với điều kiện lọc</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '10px' }}>
                            <button type="button" className="btn-cancel" style={{ border: 'none', background: 'none', color: '#e74c3c', margin: 0 }} onClick={handleClearAdvancedFilters}>
                                <i className="fa-solid fa-eraser"></i> Xóa bộ lọc
                            </button>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#3498db', margin: 0 }} onClick={handlePreviewAdvancedFilters}>
                                    <i className="fa-solid fa-magnifying-glass"></i> Lọc & Xem trước
                                </button>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#1a9953', margin: 0 }} onClick={handleExportFromModal}>
                                    <i className="fa-solid fa-download"></i> Xuất Excel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookCompilationManagement;